import { CombinedFilter } from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';

import { map, tap } from 'rxjs';
import get from 'lodash-es/get';
import { format, parseISO } from 'date-fns';
import { LegacyExportTask } from '../../core/export-task-legacy';
import { ExportStoreService } from '../../../export-overlay.store';
import { ExportFileFormat, ExportRange } from '../../core/export-task.enum';

import AccountStatementsAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/account-statements/account-statements.entity';
import { OpentItemsService } from 'app/modules/components/reports/historic/open-item/open-items.service';




/** This class is instantiated by a factory (see ExportService).
 * The base ExportTask class takes care of all the functionality of the
 * task, like the current page, the progress and the status of the
 * task. */
export class OpenItemExport extends LegacyExportTask {
  constructor(
    itemCount: number,
    initialFilter: CombinedFilter<AccountStatementsAttributes>,
    range: ExportRange,
    format: ExportFileFormat,
    store: ExportStoreService,
    private openItemsService: OpentItemsService,
    exportId: string = 'open-items',
    title: string = 'Partidas Abiertas'
  ) {
    super(
      exportId,
      title,
      itemCount,
      range,
      format,
      initialFilter,
      store
    );
  }

  private transformObject(item: AccountStatementsAttributes) {
    return {
      businessPartner: get(item, "businessPartner.name", "??"),
      documentType: get(item, "documentType.name", "??"),
      document: get(item, "document", "??"),
      codeReason: get(item, "codeReason", "??"),
      creationDate: get(item, "creationDate", "??"),
      expirationDate: get(item, "expirationDate", "??"),
      paymentCondition: get(item, "paymentCondition", "??"),
      reference: get(item, "reference", "??"),
      additionalOC: get(item, "additionalOC", "??"),
      totalAmount: get(item, "totalAmount", "??"),
      balance: get(item, "balance", "??"),
    };
  }

  /** Better header text for excel and csv */
  private transformObjectAsNatural(item: AccountStatementsAttributes) {

    let expirationDate = get(item, "expirationDate", "??");
    return {
        "Socio de Negocios": get(item, "businessPartner.name", "??"),
        "Nro. Doc.": `${get(item, 'documentType', '??')}-${get(item, 'document', '??')}`,
        "Tipo": get(item, "codeReason", "??"),
        "Fecha de Creación": this.formatDate(String(get(item, "creationDate", "??"))),
        "Fecha de Vencimiento": this.formatDate(String(get(item, "expirationDate", "??"))),
        "Días vencidos": expirationDate ? this.getExpiredDays(expirationDate as Date) : "0",
        "Condición de Pago": get(item, "paymentCondition.name", ""),
        "Referencia": this.getReference(item),
        "Total": get(item, "totalAmount", "0"),
        "Saldo": get(item, "balance", "0"),
    };
  }

  private getReference(item: AccountStatementsAttributes): string {
    let reference = get(item, "reference", "").trim();
    let additionalOC = get(item, "additionalOC", "").trim();
    return [reference, additionalOC].filter(Boolean).join(" - ") || "??";
  }

  private formatDate(dateString: string, includeTime: boolean = false): string {
    if (dateString === "??") return dateString;
    const dateFormat = includeTime ? "dd/MM/yyyy hh:mm a" : "dd/MM/yyyy";
    return format(parseISO(dateString), dateFormat);
  }

  // Función para calcular los días vencidos
  private getExpiredDays(expirationDate: Date): number {
    if (!expirationDate) {
      return 0;
    }

    const today = new Date();
    const expiration = new Date(expirationDate);

    today.setHours(0, 0, 0, 0);
    expiration.setHours(0, 0, 0, 0);

    if (isNaN(expiration.getTime())) {
      return 0; // Si la fecha es inválida, retorna 0 o el valor que consideres adecuado
    }

    const timeDifference = today.getTime() - expiration.getTime();
    const dayDifference = timeDifference / (1000 * 3600 * 24);
    return dayDifference > 0 ? Math.floor(dayDifference) : 0;
  }

  /** The main method of the export task class. Called for each page. Here do the request, process
   * and transform the data. The result for each request after processed must be an object array,
   * this object array will be later transformed to a file (xlsx, csv, json) automatically. */
  override onRequest(
    currentFilter: CombinedFilter<AccountStatementsAttributes>
  ) {
    // To avoid displaying the global loading screen, the header x-ave-ctx is sent with the request,
    // the http interceptor finds that the header is set as 'export' and ignores the request, so
    // won't display the loading screen.
    return this.openItemsService
      .findByFilter(currentFilter, { contextHeader: "export" })
      .pipe(
        tap((response) => {
          if (this.format === ExportFileFormat.JSON) {
            this.addToSection(
              this.title,
              response?.data?.[0]?.data?.map((item) => this.transformObject(item))
            );
          } else {
            this.addToSection(
              this.title,
              response?.data?.[0]?.data?.map((item) => this.transformObjectAsNatural(item))
            );
          }
        })
      );
  }
}